import React, { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { useConfirm } from "../contexts/ConfirmContext";
import {
  UserPlus, Edit2, Trash2, X, Save, UserCheck, UserX, Shield, KeyRound,
  Copy, Check, Users, Search, Loader, Mail, Hash,
} from "lucide-react";
import { toast, Toaster } from "sonner";

// ── Helpers ────────────────────────────────────────────────
const ROLE_META = {
  admin:      { label: "Administrator", desc: "Vollzugriff auf den Workspace", color: "bg-primary/15 text-primary" },
  technician: { label: "Techniker",     desc: "Lesen + Schreiben (konfigurierbar)", color: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
};

const generatePassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const specials = "!@#$%&*?";
  let pw = "";
  for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  pw += specials[Math.floor(Math.random() * specials.length)];
  pw += Math.floor(Math.random() * 10);
  return pw;
};

const deriveInitials = (name) =>
  (name || "")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map(p => p[0]).join("").toUpperCase() || "MM";

// ── Component ──────────────────────────────────────────────
export default function UsersPage() {
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [modal, setModal] = useState(null); // { type: "invite"|"edit"|"password", user? }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => users.filter(u => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return [u.name, u.email, u.initials].some(v => (v || "").toLowerCase().includes(q));
  }), [users, query, roleFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    admins: users.filter(u => u.role === "admin").length,
    technicians: users.filter(u => u.role === "technician").length,
    active: users.filter(u => u.active).length,
  }), [users]);

  const handleDelete = async (u) => {
    if (!(await confirm({
      title: "Mitglied entfernen",
      message: `"${u.name}" wird aus dem Workspace ${me?.tenant_name} entfernt. Der Login wird deaktiviert.`,
      confirmLabel: "Entfernen",
    }))) return;
    try {
      await api.delete(`/users/${u._id}`);
      toast.success("Mitglied entfernt");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <Toaster richColors position="top-right" />

      {/* Hero */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users size={26} className="text-primary" /> Team-Mitglieder
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Workspace <strong>{me?.tenant_name}</strong> — Mitglieder einladen, Rollen & Kürzel verwalten
          </p>
        </div>
        <button data-testid="invite-member-button"
          onClick={() => setModal({ type: "invite" })}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors shadow-soft">
          <UserPlus size={16} /> Mitglied einladen
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Mitglieder gesamt" value={stats.total} icon={Users} />
        <Stat label="Administratoren" value={stats.admins} icon={Shield} accent="primary" />
        <Stat label="Techniker" value={stats.technicians} icon={UserCheck} accent="info" />
        <Stat label="Aktiv" value={stats.active} icon={UserCheck} accent="success" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="members-search" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Suchen: Name, E-Mail, Kürzel…"
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-card border border-border focus:border-primary focus:outline-none" />
        </div>
        <div className="flex gap-1 bg-muted/30 p-1 rounded">
          {[["all", "Alle"], ["admin", "Admin"], ["technician", "Techniker"]].map(([k, l]) => (
            <button key={k} onClick={() => setRoleFilter(k)} data-testid={`filter-role-${k}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${roleFilter === k ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Members Grid/Table */}
      <div className="bg-card border border-border shadow-soft" data-testid="members-list">
        {loading ? (
          <div className="p-12 flex items-center justify-center"><Loader size={22} className="animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={40} className="mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-sm font-semibold">Keine Mitglieder gefunden</p>
            <p className="text-xs text-muted-foreground mt-1">
              {query ? "Andere Suche versuchen" : "Lade dein erstes Team-Mitglied ein"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map(u => {
              const isMe = u._id === me?._id;
              const meta = ROLE_META[u.role] || ROLE_META.technician;
              return (
                <div key={u._id} data-testid={`member-row-${u._id}`} className="group flex items-center gap-4 px-5 py-4 hover:bg-muted/20 transition-colors">
                  <div className="initials-avatar w-12 h-12 text-base flex-shrink-0">{u.initials}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold truncate">{u.name}</p>
                      {isMe && <span className="text-[10px] uppercase font-bold tracking-wider bg-primary/15 text-primary px-1.5 py-0.5">Du</span>}
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${meta.color}`}>{meta.label}</span>
                      {u.invite_pending && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-700 dark:text-amber-300 px-1.5 py-0.5" data-testid={`pending-badge-${u._id}`}>Einladung offen</span>}
                      {!u.active && <span className="text-[10px] font-bold uppercase tracking-wider bg-muted text-muted-foreground px-1.5 py-0.5">Inaktiv</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                      <span className="flex items-center gap-1"><Mail size={11} /> {u.email || "—"}</span>
                      <span className="flex items-center gap-1"><Hash size={11} /> {u.initials}</span>
                      {u.role === "technician" && (
                        <span>{u.permissions?.read ? "📖 Lesen " : ""}{u.permissions?.write ? "✏️ Schreiben" : ""}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                    {u.invite_pending && (
                      <IconBtn title="Einladung erneut senden" onClick={async () => {
                        try {
                          const r = await api.post(`/users/invite/${u._id}/resend`);
                          if (r.data.email_sent) toast.success("Einladung erneut gesendet");
                          else if (r.data.accept_url) {
                            navigator.clipboard?.writeText(r.data.accept_url);
                            toast.info("Email-Versand nicht konfiguriert — Link in Zwischenablage kopiert");
                          }
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }} testId={`member-resend-${u._id}`}><Mail size={13} /></IconBtn>
                    )}
                    <IconBtn title="Bearbeiten" onClick={() => setModal({ type: "edit", user: u })} testId={`member-edit-${u._id}`}><Edit2 size={13} /></IconBtn>
                    <IconBtn title="Passwort zurücksetzen" onClick={() => setModal({ type: "password", user: u })} testId={`member-reset-${u._id}`}><KeyRound size={13} /></IconBtn>
                    {!isMe && (
                      <IconBtn title="Entfernen" danger onClick={() => handleDelete(u)} testId={`member-delete-${u._id}`}><Trash2 size={13} /></IconBtn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal?.type === "invite" && <InviteModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "edit" && <EditModal user={modal.user} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "password" && <PasswordModal user={modal.user} onClose={() => setModal(null)} />}
    </div>
  );
}

// ── Invite Modal ────────────────────────────────────────────
function InviteModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", initials: "", role: "technician", permissions: { read: true, write: true } });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Auto-derive initials from name
  const handleName = (v) => setForm(f => ({ ...f, name: v, initials: f.initials || deriveInitials(v) }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/users/invite", form);
      setResult(r.data);
      if (r.data.email_sent) {
        toast.success(`Einladungs-Email an ${form.email} gesendet`);
      } else {
        toast.info("Email-Versand nicht konfiguriert — teile den Link manuell");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    if (result?.accept_url) { navigator.clipboard?.writeText(result.accept_url); toast.success("Link kopiert"); }
  };

  if (result) {
    return (
      <Modal onClose={() => { onClose(); onSaved(); }} title={result.email_sent ? "Einladung versendet" : "Einladung erstellt"} icon={UserPlus} testId="invite-result-modal">
        <div className="space-y-4">
          {result.email_sent ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 px-4 py-3">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold mb-1">
                <Check size={15} /> Email wurde gesendet
              </div>
              <p className="text-xs text-muted-foreground">
                <strong>{form.name}</strong> ({form.email}) erhält in Kürze eine E-Mail mit Aktivierungs-Link. Der Link ist 72 Stunden gültig.
              </p>
            </div>
          ) : (
            <div className="bg-amber-500/10 border border-amber-500/30 px-4 py-3 space-y-3">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Email-Versand nicht konfiguriert</p>
              <p className="text-xs text-muted-foreground">
                Teile diesen Aktivierungs-Link mit <strong>{form.email}</strong>:
              </p>
              <div className="flex gap-2">
                <input readOnly value={result.accept_url || ""} data-testid="invite-accept-url"
                  className="flex-1 px-3 py-2 text-xs font-mono bg-background border border-input" />
                <button onClick={copyLink} data-testid="copy-accept-url" className="px-3 py-2 border border-border hover:border-primary text-xs font-semibold flex items-center gap-1">
                  <Copy size={13} /> Kopieren
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Tipp: Du kannst Resend unter <strong>Einstellungen</strong> konfigurieren, um Einladungen automatisch per E-Mail zu senden.
              </p>
            </div>
          )}
          <button onClick={() => { onClose(); onSaved(); }} className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
            Fertig
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title="Mitglied einladen" icon={UserPlus} testId="invite-modal">
      <form onSubmit={submit} className="space-y-4">
        <div className="bg-sky-500/10 border border-sky-500/30 px-3 py-2.5 text-xs text-sky-900 dark:text-sky-300 flex items-start gap-2">
          <Mail size={13} className="flex-shrink-0 mt-0.5" />
          <span>Der Kollege erhält eine Email mit Aktivierungs-Link und setzt sein Passwort selbst (sicherer als geteilte Passwörter).</span>
        </div>
        <Field label="Name" required value={form.name} onChange={handleName} placeholder="Anna Meier" testId="invite-name" />
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="E-Mail" required type="email" value={form.email} onChange={v => setForm({ ...form, email: v.toLowerCase() })} placeholder="anna@firma.ch" testId="invite-email" />
          </div>
          <Field label="Kürzel" required value={form.initials} onChange={v => setForm({ ...form, initials: v.slice(0, 4).toUpperCase() })} placeholder="AM" testId="invite-initials" maxLength={4} style={{ textTransform: "uppercase" }} />
        </div>

        <RolePicker role={form.role} onChange={role => setForm({ ...form, role })} testPrefix="invite" />

        {form.role === "technician" && (
          <PermissionsPicker perms={form.permissions} onChange={permissions => setForm({ ...form, permissions })} />
        )}

        <div className="flex gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-border hover:bg-muted text-sm font-semibold">Abbrechen</button>
          <button data-testid="invite-submit" type="submit" disabled={busy} className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />}
            Einladung senden
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit Modal ──────────────────────────────────────────────
function EditModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    initials: user.initials || "",
    role: user.role || "technician",
    permissions: { ...(user.permissions || { read: true, write: false }) },
    active: user.active !== false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put(`/users/${user._id}`, form);
      toast.success("Mitglied aktualisiert");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Mitglied bearbeiten" icon={Edit2} testId="edit-modal" subtitle={user.email}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" required value={form.name} onChange={v => setForm({ ...form, name: v })} testId="edit-name" />
        <Field label="Kürzel" required value={form.initials} onChange={v => setForm({ ...form, initials: v.slice(0, 4).toUpperCase() })} testId="edit-initials" maxLength={4} style={{ textTransform: "uppercase" }} />

        <RolePicker role={form.role} onChange={role => setForm({ ...form, role })} testPrefix="edit" />

        {form.role === "technician" && (
          <PermissionsPicker perms={form.permissions} onChange={permissions => setForm({ ...form, permissions })} />
        )}

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="edit-active" className="w-4 h-4" />
          <span className="text-sm">Account aktiv (kann sich anmelden)</span>
        </label>

        <div className="flex gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-border hover:bg-muted text-sm font-semibold">Abbrechen</button>
          <button data-testid="edit-submit" type="submit" disabled={busy} className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader size={14} className="animate-spin" /> : <Save size={14} />} Speichern
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Password Reset Modal ────────────────────────────────────
function PasswordModal({ user, onClose }) {
  const [pw, setPw] = useState(generatePassword());
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put(`/users/${user._id}/password`, { password: pw });
      navigator.clipboard?.writeText(`Login: ${user.email}\nNeues Passwort: ${pw}`).catch(() => {});
      toast.success("Passwort zurückgesetzt — Zugangsdaten in Zwischenablage kopiert");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Passwort zurücksetzen" icon={KeyRound} testId="password-modal" subtitle={`Für ${user.name} (${user.email})`}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Neues Passwort</label>
          <div className="flex gap-2">
            <input data-testid="reset-password" value={pw} onChange={e => setPw(e.target.value)} required
              className="flex-1 px-3 py-2.5 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none" />
            <button type="button" onClick={() => setPw(generatePassword())} className="px-3 py-2.5 border border-border hover:border-primary text-xs font-semibold">↻</button>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(pw); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="px-3 py-2.5 border border-border hover:border-primary text-xs font-semibold flex items-center gap-1">
              {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Der Nutzer wird beim nächsten Login gezwungen, das Passwort zu ändern.</p>
        </div>

        <div className="flex gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-border hover:bg-muted text-sm font-semibold">Abbrechen</button>
          <button data-testid="reset-submit" type="submit" disabled={busy} className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader size={14} className="animate-spin" /> : <KeyRound size={14} />} Zurücksetzen
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Sub-Components ──────────────────────────────────────────
function Stat({ label, value, icon: Icon, accent = "default" }) {
  const colors = {
    default: "text-muted-foreground bg-muted/30",
    primary: "bg-primary/10 text-primary",
    info: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
    success: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  };
  return (
    <div className="bg-card border border-border shadow-soft p-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-md ${colors[accent]}`}><Icon size={16} /></div>
      <div>
        <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</p>
      </div>
    </div>
  );
}

function Modal({ title, subtitle, icon: Icon, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
      <div className="bg-card border border-border shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-primary/10 text-primary rounded-md"><Icon size={15} /></div>
            <div>
              <h3 className="text-base font-bold tracking-tight">{title}</h3>
              {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", required, value, onChange, placeholder, testId, maxLength, style }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">{label}{required && " *"}</label>
      <input data-testid={testId} type={type} required={required} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} style={style}
        className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none transition-colors" />
    </div>
  );
}

function RolePicker({ role, onChange, testPrefix }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Rolle</label>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(ROLE_META).map(([k, m]) => (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testPrefix}-role-${k}`}
            className={`text-left p-3 border transition-colors ${role === k ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-7 h-7 rounded-md flex items-center justify-center ${m.color}`}>
                {k === "admin" ? <Shield size={13} /> : <UserCheck size={13} />}
              </div>
              <span className="text-sm font-semibold">{m.label}</span>
            </div>
            <p className="text-[11px] text-muted-foreground">{m.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function PermissionsPicker({ perms, onChange }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Berechtigungen</label>
      <div className="space-y-2">
        <Perm label="Leserecht" desc="Alle Kunden-Daten ansehen" checked={perms.read} onChange={v => onChange({ ...perms, read: v })} testId="perm-read" />
        <Perm label="Schreibrecht" desc="Kunden, Tickets, Dokumente erstellen/bearbeiten" checked={perms.write} onChange={v => onChange({ ...perms, write: v })} testId="perm-write" />
      </div>
    </div>
  );
}

function Perm({ label, desc, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 p-3 border border-border hover:border-primary/50 cursor-pointer transition-colors">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} data-testid={testId} className="w-4 h-4 mt-0.5" />
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </label>
  );
}

function IconBtn({ children, onClick, title, danger, testId }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId}
      className={`p-2 border border-transparent hover:border-border rounded transition-colors ${danger ? "hover:text-destructive hover:border-destructive/50" : "text-muted-foreground hover:text-primary"}`}>
      {children}
    </button>
  );
}
